import './equity-cap-table.css';
import StartupPageTitle from '../../../components/StartupPageTitle';
import { ChartPieSlice as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ChartPieSlice,
  CheckCircle,
  DownloadSimple,
  FileCsv,
  FloppyDisk,
  Plus,
  Trash,
  UploadSimple,
  WarningCircle,
} from '@phosphor-icons/react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import api from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import EquityInvitesCard from './EquityInvitesCard';

const HOLDER_TYPES = [
  { value: 'founder', label: 'Founder' },
  { value: 'investor', label: 'Investor' },
  { value: 'employee', label: 'Employee' },
  { value: 'esop', label: 'ESOP Pool' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'other', label: 'Other' },
];

const COLORS = ['#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#0F766E', '#115E59', '#99F6E4', '#134E4A'];

const emptyRow = () => ({
  id: `local-${Date.now()}-${Math.random()}`,
  name: '',
  holder_type: 'founder',
  ownership_pct: '',
  shares: '',
  invested_amount: '',
  security_type: 'Common',
  notes: '',
});

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
};

const parseCsv = (text) => {
  const lines = text
    .replace(/\r/g, '')
    .split('\n')
    .filter((line) => line.trim() && !line.trim().startsWith('#'));
  if (lines.length < 2) throw new Error('CSV needs the template header and at least one holder');

  const headers = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const required = ['holder_name', 'holder_type', 'ownership_pct'];
  const missing = required.filter((key) => !headers.includes(key));
  if (missing.length) throw new Error(`Missing columns: ${missing.join(', ')}`);

  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line);
    const row = Object.fromEntries(headers.map((header, i) => [header, values[i] ?? '']));
    const pct = Number(row.ownership_pct);
    if (!row.holder_name || !Number.isFinite(pct)) {
      throw new Error(`Invalid data on CSV row ${index + 2}`);
    }
    return {
      id: `csv-${index}-${Date.now()}`,
      name: row.holder_name,
      holder_type: (row.holder_type || 'other').toLowerCase(),
      ownership_pct: pct,
      shares: row.shares ? Number(row.shares) : '',
      invested_amount: row.invested_amount ? Number(row.invested_amount) : '',
      security_type: row.security_type || '',
      notes: row.notes || '',
    };
  });
};

const EquityDashboard = () => {
  const { formatCompact } = useCurrency();
  const fileRef = useRef(null);
  const [holders, setHolders] = useState([]);
  const [valuation, setValuation] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await api.get('/startup/equity-cap-table');
      setHolders((res.data?.holders || []).map((row) => ({ ...row, id: row.id || `holder-${Math.random()}` })));
      setValuation(res.data?.current_valuation ?? '');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load equity data');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalPct = useMemo(
    () => holders.reduce((sum, row) => sum + (Number(row.ownership_pct) || 0), 0),
    [holders]
  );
  const unallocated = Math.max(0, 100 - totalPct);
  const overAllocated = totalPct > 100.01;

  const chartData = useMemo(() => {
    const rows = holders
      .filter((row) => row.name && Number(row.ownership_pct) > 0)
      .map((row) => ({ name: row.name, value: Number(row.ownership_pct) }));
    if (unallocated > 0.009) rows.push({ name: 'Unallocated', value: unallocated });
    return rows;
  }, [holders, unallocated]);

  const founderPct = useMemo(
    () => holders.filter((h) => h.holder_type === 'founder').reduce((s, h) => s + (Number(h.ownership_pct) || 0), 0),
    [holders]
  );
  const investorPct = useMemo(
    () => holders.filter((h) => h.holder_type === 'investor').reduce((s, h) => s + (Number(h.ownership_pct) || 0), 0),
    [holders]
  );
  const esopPct = useMemo(
    () => holders.filter((h) => h.holder_type === 'esop').reduce((s, h) => s + (Number(h.ownership_pct) || 0), 0),
    [holders]
  );

  const updateRow = (id, field, value) => {
    setHolders((rows) => rows.map((row) => row.id === id ? { ...row, [field]: value } : row));
    setDirty(true);
  };

  const addRow = () => {
    setHolders((rows) => [...rows, emptyRow()]);
    setDirty(true);
  };

  const removeRow = (id) => {
    setHolders((rows) => rows.filter((row) => row.id !== id));
    setDirty(true);
  };

  const save = async () => {
    const clean = holders
      .filter((row) => row.name.trim() || Number(row.ownership_pct))
      .map((row) => ({
        name: row.name.trim(),
        holder_type: row.holder_type,
        ownership_pct: Number(row.ownership_pct) || 0,
        shares: row.shares === '' ? null : Number(row.shares),
        invested_amount: row.invested_amount === '' ? null : Number(row.invested_amount),
        security_type: row.security_type?.trim() || null,
        notes: row.notes?.trim() || null,
        vc_fund_id: row.vc_fund_id || null,
        managed_fund_id: row.managed_fund_id || null,
        source_invite_ids: row.source_invite_ids || [],
      }));

    if (!clean.length) {
      toast.error('Add at least one equity holder');
      return;
    }
    if (clean.some((row) => !row.name)) {
      toast.error('Every equity holder needs a name');
      return;
    }
    if (overAllocated) {
      toast.error(`Ownership is ${totalPct.toFixed(2)}%. Reduce it to 100% or less.`);
      return;
    }

    setSaving(true);
    try {
      const res = await api.put('/startup/equity-cap-table', {
        holders: clean,
        current_valuation: valuation === '' ? null : Number(valuation),
      });
      setHolders((res.data?.holders || clean).map((row) => ({ ...row, id: row.id || `holder-${Math.random()}` })));
      setDirty(false);
      toast.success('Equity structure saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save equity structure');
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const parsed = parseCsv(await file.text());
      const importedTotal = parsed.reduce((sum, row) => sum + (Number(row.ownership_pct) || 0), 0);
      if (importedTotal > 100.01) throw new Error(`CSV ownership totals ${importedTotal.toFixed(2)}%, which exceeds 100%`);
      setHolders(parsed);
      setDirty(true);
      toast.success(`Imported ${parsed.length} equity holders. Review and save.`);
    } catch (e) {
      toast.error(e.message || 'Could not read CSV');
    }
  };

  const downloadTemplate = () => {
    const template = [
      '# FounderBoard Equity Import Template',
      '# REQUIRED: holder_name, holder_type, ownership_pct',
      '# holder_type must be one of: founder, investor, employee, esop, advisor, other',
      '# ownership_pct is a number from 0 to 100; all rows combined cannot exceed 100',
      '# OPTIONAL: shares, invested_amount, security_type, notes',
      '# security_type examples: Common, Preferred, Seed Preferred, SAFE Converted, Options',
      '# Use one row per holder. Leave optional cells blank when unavailable.',
      '# FounderBoard ignores rows beginning with #, so these instructions may remain in the file.',
      'holder_name,holder_type,ownership_pct,shares,invested_amount,security_type,notes',
      ',founder,,,,Common,',
      ',investor,,,,Preferred,',
      ',esop,,,,Options,',
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'FounderBoard_equity_import_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[1280px] px-3 py-6 sm:px-4 md:px-5">
        <div className="h-36 animate-pulse rounded-xl border border-border bg-surface" />
        <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="h-96 animate-pulse rounded-xl border border-border bg-surface" />
          <div className="h-96 animate-pulse rounded-xl border border-border bg-surface" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-3 py-5 sm:px-4 md:px-5" data-testid="equity-page">
      <section className="equity-page-header startup-page-header">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <StartupPageTitle icon={PageHeadingIcon} className="font-heading text-[24px] font-bold tracking-[-0.035em] text-text-primary sm:text-[28px]">Equity</StartupPageTitle>
            <p className="mt-1 max-w-[650px] text-[11px] leading-5 text-text-muted sm:text-[12px]">
              Manage your cap table, track shareholders, and keep ownership up to date.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-[10px] font-bold text-text-secondary transition hover:border-[#0D9488]/30 hover:text-[#0D9488]" style={{ backgroundColor: 'var(--card)' }}
            >
              <DownloadSimple size={13} weight="bold" /> Download Template
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#0D9488]/20 bg-[#0D9488]/[0.055] px-3 text-[10px] font-bold text-[#0D9488] transition hover:bg-[#0D9488]/10"
            >
              <UploadSimple size={13} weight="bold" /> Upload CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} />
            <button
              type="button"
              onClick={save}
              disabled={saving || overAllocated || !dirty}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#0D9488] px-3.5 text-[10px] font-bold text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:opacity-45"
            >
              <FloppyDisk size={13} weight="bold" /> {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </section>

      <div className="mt-3 grid grid-cols-2 gap-2.5 md:grid-cols-4">
        {[
          ['Total allocated', `${totalPct.toFixed(2)}%`],
          ['Founders', `${founderPct.toFixed(2)}%`],
          ['Investors', `${investorPct.toFixed(2)}%`],
          ['ESOP', `${esopPct.toFixed(2)}%`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-border p-3.5" style={{ backgroundColor: 'var(--card)' }}>
            <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-text-muted">{label}</div>
            <div className="mt-1.5 font-heading text-[20px] font-bold text-text-primary">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <section className="equity-cap-editor rounded-xl border border-border p-3.5 sm:p-4" style={{ backgroundColor: 'var(--card)' }}>
          <div className="equity-cap-heading mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-[13px] font-bold text-text-primary">Cap table</h2>
              <p className="mt-0.5 text-[10px] text-text-muted">Edit manually or upload a completed FounderBoard equity template.</p>
            </div>
            <button
              type="button"
              onClick={addRow}
              className="equity-add-holder inline-flex h-8 items-center gap-1 rounded-md border border-[#0D9488]/20 bg-[#0D9488]/[0.05] px-2.5 text-[9px] font-bold text-[#0D9488]"
            >
              <Plus size={11} weight="bold" /> Add holder
            </button>
          </div>

          <div className="equity-cap-guide mb-3 grid gap-2 rounded-lg border border-[#0D9488]/15 bg-[#0D9488]/[0.025] p-3 sm:grid-cols-3">
            <div>
              <div className="text-[8px] font-extrabold uppercase tracking-[0.1em] text-[#0D9488]">Required</div>
              <div className="mt-1 text-[9px] leading-4 text-text-muted">Holder name, holder type and ownership percentage.</div>
            </div>
            <div>
              <div className="text-[8px] font-extrabold uppercase tracking-[0.1em] text-[#0D9488]">Optional</div>
              <div className="mt-1 text-[9px] leading-4 text-text-muted">Shares, invested amount, security type and notes.</div>
            </div>
            <div>
              <div className="text-[8px] font-extrabold uppercase tracking-[0.1em] text-[#0D9488]">Validation</div>
              <div className="mt-1 text-[9px] leading-4 text-text-muted">Total ownership can be below 100%, but can never exceed it.</div>
            </div>
          </div>

          {overAllocated && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-500/20 bg-rose-500/[0.06] p-2.5 text-[10px] text-rose-700 dark:text-rose-300">
              <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
              Ownership totals {totalPct.toFixed(2)}%. Reduce the table to 100% or less before saving.
            </div>
          )}

          {!holders.length ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex min-h-[210px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#0D9488]/20 bg-[#0D9488]/[0.025] p-5 text-center"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0D9488]/10 text-[#0D9488]">
                <FileCsv size={20} weight="bold" />
              </span>
              <div className="mt-3 text-[12px] font-bold text-text-primary">Upload a cap table CSV</div>
              <div className="mt-1 max-w-[320px] text-[10px] leading-4 text-text-muted">Download the detailed template, complete one row per holder, then upload it here.</div>
            </button>
          ) : (
            <div className="equity-cap-scroll" role="region" aria-label="Editable cap table" tabIndex={0}>
              <table className="equity-cap-table">
                <colgroup>
                  <col className="w-[20%]" />
                  <col className="w-[13%]" />
                  <col className="w-[12%]" />
                  <col className="w-[12%]" />
                  <col className="w-[14%]" />
                  <col className="w-[24%]" />
                  <col className="w-[5%]" />
                </colgroup>
                <thead>
                  <tr className="text-left text-[8px] font-bold uppercase tracking-[0.1em] text-text-muted">
                    <th className="px-2">Holder</th>
                    <th className="px-2">Type</th>
                    <th className="px-1.5">Ownership %</th>
                    <th className="px-2">Shares</th>
                    <th className="px-2">Invested</th>
                    <th className="px-2">Security</th>
                    <th className="w-10"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {holders.map((row) => (
                    <tr key={row.id} className="equity-holder-row">
                      <td className="rounded-l-lg border-y border-l border-border p-1.5">
                        <input aria-label={`Holder name for ${row.name || "unnamed holder"}`} value={row.name} onChange={(e) => updateRow(row.id, 'name', e.target.value)} placeholder="Holder name" className="min-w-0 w-full bg-transparent px-1 py-1.5 text-[10px] font-semibold text-text-primary outline-none" />
                      </td>
                      <td className="border-y border-border p-1.5">
                        <select aria-label={`Holder type for ${row.name || "unnamed holder"}`} value={row.holder_type} onChange={(e) => updateRow(row.id, 'holder_type', e.target.value)} className="min-w-0 w-full rounded-md border border-border px-1.5 py-1.5 text-[9px] text-text-secondary outline-none" style={{ backgroundColor: 'var(--card)' }}>
                          {HOLDER_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select>
                      </td>
                      <td className="border-y border-border p-1.5">
                        <input aria-label={`Ownership percentage for ${row.name || "unnamed holder"}`} type="number" min="0" max="100" step="0.01" value={row.ownership_pct} onChange={(e) => updateRow(row.id, 'ownership_pct', e.target.value)} className="min-w-0 w-full rounded-md border border-border px-1.5 py-1.5 text-[9px] font-mono text-text-primary outline-none focus:border-[#0D9488]" style={{ backgroundColor: 'var(--card)' }} />
                      </td>
                      <td className="border-y border-border p-1.5">
                        <input aria-label={`Shares for ${row.name || "unnamed holder"}`} type="number" min="0" value={row.shares ?? ''} onChange={(e) => updateRow(row.id, 'shares', e.target.value)} placeholder="—" className="min-w-0 w-full rounded-md border border-border px-1.5 py-1.5 text-[9px] text-text-primary outline-none" style={{ backgroundColor: 'var(--card)' }} />
                      </td>
                      <td className="border-y border-border p-1.5">
                        <input aria-label={`Invested amount for ${row.name || "unnamed holder"}`} type="number" min="0" value={row.invested_amount ?? ''} onChange={(e) => updateRow(row.id, 'invested_amount', e.target.value)} placeholder="—" className="min-w-0 w-full rounded-md border border-border px-1.5 py-1.5 text-[9px] text-text-primary outline-none" style={{ backgroundColor: 'var(--card)' }} />
                      </td>
                      <td className="border-y border-border p-1.5">
                        <input aria-label={`Security type for ${row.name || "unnamed holder"}`} value={row.security_type ?? ''} onChange={(e) => updateRow(row.id, 'security_type', e.target.value)} placeholder="Common / SAFE…" className="min-w-0 w-full rounded-md border border-border px-1.5 py-1.5 text-[9px] text-text-primary outline-none" style={{ backgroundColor: 'var(--card)' }} />
                      </td>
                      <td className="rounded-r-lg border-y border-r border-border p-1.5 text-right">
                        <button type="button" onClick={() => removeRow(row.id)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition hover:bg-rose-500/10 hover:text-rose-600" title="Remove holder" aria-label={`Remove ${row.name || "unnamed holder"}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="equity-cap-footer mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold ${overAllocated ? 'text-rose-600' : totalPct >= 99.99 ? 'text-emerald-600' : 'text-text-muted'}`}>
              {overAllocated ? <WarningCircle size={13} weight="fill" /> : <CheckCircle size={13} weight="bold" />}
              {totalPct >= 99.99 && totalPct <= 100.01 ? 'Cap table totals 100%' : `${unallocated.toFixed(2)}% unallocated`}
            </div>

            <label className="flex items-center gap-2 text-[10px] text-text-muted">
              Current valuation
              <input
                type="number"
                min="0"
                value={valuation}
                onChange={(e) => { setValuation(e.target.value); setDirty(true); }}
                placeholder="Optional"
                className="w-[160px] rounded-md border border-border px-2.5 py-2 text-[10px] font-semibold text-text-primary outline-none focus:border-[#0D9488]" style={{ backgroundColor: 'var(--card)' }}
              />
            </label>
          </div>
        </section>

        <section className="rounded-xl border border-border p-3.5 sm:p-4" style={{ backgroundColor: 'var(--card)' }}>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0D9488]/10 text-[#0D9488]">
              <ChartPieSlice size={16} weight="fill" />
            </span>
            <div>
              <h2 className="text-[12px] font-bold text-text-primary">Ownership breakup</h2>
              <div className="text-[9px] text-text-muted">{holders.length} holder{holders.length === 1 ? '' : 's'}</div>
            </div>
          </div>

          <div className="relative mt-3 h-[250px]">
            {chartData.length ? (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={2} stroke="var(--card)" strokeWidth={2} isAnimationActive animationDuration={900}>
                      {chartData.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Ownership']} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--foreground)', fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-heading text-[22px] font-bold text-text-primary">{totalPct.toFixed(1)}%</div>
                    <div className="text-[8px] font-bold uppercase tracking-[0.1em] text-text-muted">Allocated</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-[10px] text-text-muted">Add holders to preview ownership.</div>
            )}
          </div>

          <div className="mt-2 space-y-1.5">
            {chartData.slice(0, 7).map((item, index) => (
              <div key={`${item.name}-${index}`} className="flex items-center gap-2 text-[10px]">
                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: COLORS[index % COLORS.length] }} />
                <span className="min-w-0 flex-1 truncate text-text-secondary">{item.name}</span>
                <span className="font-mono font-bold text-text-primary">{Number(item.value).toFixed(2)}%</span>
              </div>
            ))}
          </div>

          {valuation !== '' && Number(valuation) > 0 && (
            <div className="mt-4 rounded-lg border border-[#0D9488]/10 bg-[#0D9488]/[0.04] p-3">
              <div className="text-[8px] font-bold uppercase tracking-[0.1em] text-text-muted">Current valuation</div>
              <div className="mt-1 font-heading text-[18px] font-bold text-text-primary">{formatCompact(Number(valuation))}</div>
            </div>
          )}
        </section>
      </div>

      <EquityInvitesCard onInviteAccepted={() => load(false)} />
    </div>
  );
};

export default EquityDashboard;
